// src/components/Navbar.jsx
//
// App-shell navbar for authenticated pages.
//
// Used by /dashboard, /my-cases, /admin — i.e. only after the clinician
// is signed in. The public landing page does not render this navbar.
//
// Layout: Brand · tab nav (Demo cases / My cases / Admin) · theme · user
// avatar menu. Tabs use an animated underline indicator; account actions
// live inside a dropdown anchored to the user avatar.

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../utils/isAdmin';
import './Navbar.css';

export default function Navbar({ theme, toggleTheme }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Scroll state — keep the visual hook even though the bar is already solid.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the user dropdown on outside-click.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [userMenuOpen]);

  // Reset any open menus when the route changes.
  useEffect(() => {
    setUserMenuOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      setUserMenuOpen(false);
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const userIsAdmin = isAdmin(user);

  // Tabs in the authenticated header. Admin tab only renders for admins.
  const appTabs = [
    { to: '/dashboard', label: 'Demo cases' },
    { to: '/my-cases',  label: 'My cases'   },
    ...(userIsAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  const initial = (user?.email?.[0] || '?').toUpperCase();

  return (
    <nav
      className={`navbar navbar--app ${scrolled ? 'navbar--scrolled' : ''}`}
    >
      <div className="container navbar__inner">
        {/* ---- Brand ---- */}
        <Link
          to={user ? '/dashboard' : '/'}
          className="navbar__brand"
          onClick={() => { setMenuOpen(false); setUserMenuOpen(false); }}
          aria-label="BrainAxis"
        >
          <img
            src="/logo-full.png"
            alt="BrainAxis — AI Precision Neurology"
            className="navbar__logo navbar__logo--full"
          />
          <img
            src="/logo-icon.png"
            alt="BrainAxis"
            className="navbar__logo navbar__logo--icon"
          />
        </Link>

        {/* ---- Mobile hamburger ---- */}
        <button
          className="navbar__menu-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span></span><span></span><span></span>
        </button>

        {/* ---- App-shell links ---- */}
        <div
          className={`navbar__links navbar__links--app ${menuOpen ? 'navbar__links--open' : ''}`}
        >
          <ul className="navtabs">
            {appTabs.map((t) => {
              const active = location.pathname === t.to;
              return (
                <li key={t.to}>
                  <Link
                    to={t.to}
                    className={`navtab ${active ? 'navtab--active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t.label}
                    {active && <span className="navtab__indicator" aria-hidden />}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="navbar__right">
            <button
              className="navbar__theme"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>

            <div className="usermenu" ref={userMenuRef}>
              <button
                className="usermenu__trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label="Account menu"
              >
                <span className="usermenu__avatar">{initial}</span>
                <span className="usermenu__caret" aria-hidden>▾</span>
              </button>

              {userMenuOpen && (
                <div className="usermenu__dropdown" role="menu">
                  <div className="usermenu__head">
                    <div className="usermenu__label">Signed in as</div>
                    <div className="usermenu__email" title={user?.email}>
                      {user?.email}
                    </div>
                    {userIsAdmin && (
                      <span className="usermenu__badge">Admin</span>
                    )}
                  </div>

                  <Link
                    to="/"
                    className="usermenu__item"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Back to public site
                  </Link>
                  <button
                    className="usermenu__item usermenu__item--danger"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
